import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { mangaList, mangaCategories } from '@/mocks/manga';

export default function MangaDetail() {
  const { mangaId } = useParams<{ mangaId: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [mangaId]);

  const manga = mangaList.find((m) => m.id === mangaId);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
            <h2 className="text-xl font-semibold text-foreground-800 mt-4">漫画未找到</h2>
            <Link
              to="/"
              className="inline-block mt-4 text-accent-500 hover:text-accent-600 text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = mangaCategories.find((c) => c.id === manga.category)?.name || '';
  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t))))
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - YY漫画网`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: '2026-01-01',
    dateModified: '2026-08-04',
    image: manga.cover,
    url: `https://yymanhua.com/manga/${manga.id}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: 'YY漫画网',
      url: 'https://yymanhua.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://yymanhua.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: categoryName,
          item: `https://yymanhua.com/category/${manga.category}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: manga.title,
          item: `https://yymanhua.com/manga/${manga.id}`,
        },
      ],
    },
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <Navbar />
      <main className="pt-16">
        <nav className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${manga.category}`} className="hover:text-accent-500 transition-colors">
              {categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{manga.title}</span>
          </div>
        </nav>

        <article className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={manga.cover}
                  alt={manga.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                {manga.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">作者：{manga.author}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  manga.status === '连载中'
                    ? 'bg-accent-100 text-accent-600'
                    : 'bg-foreground-100 text-foreground-600'
                }`}>
                  {manga.status}
                </span>
                {manga.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-6 mt-6">
                <div className="flex items-center gap-2">
                  <i className="ri-star-fill text-accent-500 text-lg"></i>
                  <div>
                    <div className="text-xl font-bold text-foreground-900">{manga.rating}</div>
                    <div className="text-xs text-foreground-500">评分</div>
                  </div>
                </div>
                <div className="w-px h-10 bg-background-300"></div>
                <div>
                  <div className="text-xl font-bold text-foreground-900">{manga.views}</div>
                  <div className="text-xs text-foreground-500">浏览量</div>
                </div>
                <div className="w-px h-10 bg-background-300"></div>
                <div>
                  <div className="text-xl font-bold text-foreground-900">{manga.updateChapter}</div>
                  <div className="text-xs text-foreground-500">最新更新</div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">漫画简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
                <button className="flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 rounded-full bg-accent-500"></div>
              <h2 className="text-lg font-bold text-foreground-950">章节目录</h2>
              <span className="text-xs text-foreground-400">共 {manga.chapters.length} 话</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {manga.chapters.map((ch) => (
                <a
                  key={ch.id}
                  href="#"
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 hover:bg-accent-50 transition-colors"
                >
                  <span className="text-sm text-foreground-700">{ch.title}</span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </a>
              ))}
            </div>
          </section>

          <section className="mt-10 mb-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-5 rounded-full bg-accent-500"></div>
              <h2 className="text-lg font-bold text-foreground-950">相关推荐</h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {relatedManga.map((rm) => (
                <Link
                  key={rm.id}
                  to={`/manga/${rm.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group"
                >
                  <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={rm.cover}
                      alt={rm.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors truncate">
                    {rm.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{rm.author}</p>
                </Link>
              ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}